import { mainnet, goerli } from 'viem/chains'
import { generatePrivateKey, privateKeyToAccount } from 'viem/accounts'
import { http, Chain, PublicClient, createPublicClient, encodeFunctionData, toHex } from 'viem'

import { Hash, AirdropList } from 'types/entities'

import { getRandomAddresses } from 'utils/helpers'
import { compareAddresses, isEther } from 'utils/crypto'

import { RPC_LIST, SupportedChainId } from 'constants/chains'
import { etherNumbers, numbers } from 'constants/variables'
import { MAD_ASSEMBLY, ZERO_ADDRESS } from 'constants/addresses'
import { MadAssembly as MadAssemblyAbi } from 'abis/MadAssembly'
import {
  Params,
  Recipient,
  DomainData,
  ValidateData,
  AirdropItem,
  AirdropOptions,
  CreateAirdropInput,
  FindExceptionsInput,
  ValidateMethodInput,
  ValidateAirdropInput,
  FindExceptionsResponse,
  GetAirdropOptionsInput,
  ValidateAirdropResponse,
  CreateAssetCalldataInput,
  CreateAirdropWithSignatureInput,
  CreateAssetCalldataInputWithSignature,
} from './types'

const maxGasLimits = {
  // ToDo check is to much
  [SupportedChainId.MAINNET]: BigInt(10_000_000),
  [SupportedChainId.GOERLI]: BigInt(10_000_000),
}

const defaultCount = {
  [mainnet.id]: BigInt(300),
  [goerli.id]: BigInt(300),
}

const MAX_ATTEMPT = 3

const EIP712Domain = [
  { name: 'name', type: 'string' },
  { name: 'version', type: 'string' },
  { name: 'chainId', type: 'uint256' },
  { name: 'verifyingContract', type: 'address' },
] as const

const Signer = [
  { name: 'owner', type: 'address' },
  { name: 'deadline', type: 'uint256' },
] as const

export class Airdropper {
  public chain: Chain
  public rpcUrl: string
  public provider: PublicClient
  private contractConfig: { address: Hash; abi: typeof MadAssemblyAbi; maxGasLimit: bigint } = {
    address: '0x',
    abi: MadAssemblyAbi,
    maxGasLimit: BigInt(7_000_000),
  }

  static getTotalAmount(acc: bigint, curr: AirdropItem) {
    acc = acc + curr.amount
    return acc
  }

  static bumpGas(estimated: bigint, percent = 10) {
    return (estimated * BigInt(numbers.ONE_HUNDRED + percent)) / BigInt(numbers.ONE_HUNDRED)
  }

  static getChunks(listLength: number | string, batchSize: number | string) {
    const _listLength = Number(listLength)
    const _batchSize = Number(batchSize)

    const batches = Math.ceil(_listLength / _batchSize)

    return Array.from({ length: batches }, (_, i) => {
      const from = i * _batchSize
      const to = from + _batchSize <= _listLength ? from + _batchSize : _listLength
      return [from, to]
    })
  }

  static filterExceptions = (acc: AirdropList, curr: Recipient) => {
    if (compareAddresses(curr.addr, ZERO_ADDRESS)) {
      return acc
    }

    acc.push({ addr: curr.addr, amount: curr.amount })
    return acc
  }

  static getSignatureCalldata(domainData: DomainData, owner: Hash, deadline: bigint) {
    const primaryType = 'Signer' as const
    return {
      primaryType,
      domain: domainData,
      message: { owner, deadline },
      types: { EIP712Domain, Signer },
    }
  }

  constructor() {
    this.chain = mainnet
    this.rpcUrl = RPC_LIST[mainnet.id]
    // мы не можем вынести провайдет в функцию потому что он не заглядывает внутрь функций
    this.provider = createPublicClient({ chain: this.chain, transport: http(this.rpcUrl) })

    this.setUpAirdropper()
  }

  public setUpAirdropper() {
    const maxGasLimit = maxGasLimits[this.chain.id as keyof typeof maxGasLimits]
    this.contractConfig = { address: MAD_ASSEMBLY[this.chain.id], abi: MadAssemblyAbi, maxGasLimit }
  }

  public initAirdropper(chain: Chain, rpcUrl: string) {
    this.chain = chain
    this.rpcUrl = rpcUrl
    this.provider = createPublicClient({ chain: this.chain, transport: http(this.rpcUrl) })

    this.setUpAirdropper()
  }

  public async getFee(length: number) {
    try {
      const coefficient = this.getPriceCoefficient(length)
      const fee = await this.readContract<bigint>('getFee', [toHex(length), toHex(coefficient)])
      return fee / BigInt(length)
    } catch (err) {
      throw err
    }
  }

  public async validateAirdrop(params: ValidateAirdropInput): Promise<ValidateAirdropResponse> {
    try {
      const { batchSize, isDeflationary } = await this.getAirdropOptions({
        token: params.token,
        customer: params.customer,
      })

      const _batchSize = batchSize >= BigInt(params.airdropList.length) ? BigInt(params.airdropList.length) : batchSize

      const response = await this.findExceptions({
        ...params,
        isDeflationary,
        batchSize: _batchSize,
        token: params.token.address,
      })

      const exceptions = response.exceptions.reduce<AirdropList>(
        (acc, curr) => Airdropper.filterExceptions(acc, curr),
        [],
      )

      return { ...response, exceptions }
    } catch (err) {
      throw err
    }
  }

  public getSignatureParams(account: Hash, selfGeneratedAddress: Hash) {
    const expiration = BigInt(Math.floor(Date.now() / 1000) + 3600)

    const domainData = {
      version: '1',
      name: 'MadAssembly',
      chainId: BigInt(this.chain.id),
      verifyingContract: this.contractConfig.address,
    }

    const data = Airdropper.getSignatureCalldata(domainData, selfGeneratedAddress, expiration)

    return {
      account,
      expiration,
      ...data,
    }
  }

  public async createAirdropTxs(params: CreateAirdropInput) {
    try {
      const txsData: Params[] = []
      const size = params.batchSize.toString()

      const fee = await this.getFee(params.airdropList.length)
      const chunks = Airdropper.getChunks(params.airdropList.length, size)

      for (const [from, to] of chunks) {
        const chunk = params.airdropList.slice(from, to)
        const total = chunk.reduce(Airdropper.getTotalAmount, etherNumbers.BG_ZERO)

        const { calldata, estimateGas, value } = await this.createAirdropTxData({
          ...params,
          total,
          airdropList: chunk,
          fee: fee * BigInt(chunk.length),
        })

        txsData.push({
          value,
          data: calldata,
          gas: estimateGas,
          from: params.customer,
          to: this.contractConfig.address,
        })
      }

      return txsData
    } catch (err) {
      throw new Error(err)
    }
  }

  public async createAirdropWithSignature(params: CreateAirdropWithSignatureInput, sender: Hash) {
    try {
      const txsData: Params[] = []
      const size = params.batchSize.toString()

      const fee = await this.getFee(params.airdropList.length)
      const chunks = Airdropper.getChunks(params.airdropList.length, size)

      for (const [from, to] of chunks) {
        const chunk = params.airdropList.slice(from, to)
        const total = chunk.reduce(Airdropper.getTotalAmount, etherNumbers.BG_ZERO)

        const { calldata, estimateGas, value } = await this.createAirdropWithSignatureTxData(
          {
            ...params,
            total,
            airdropList: chunk,
            fee: fee * BigInt(chunk.length),
          },
          sender,
        )

        txsData.push({
          value,
          data: calldata,
          gas: estimateGas,
          from: params.customer,
          to: this.contractConfig.address,
        })
      }

      return txsData
    } catch (err) {
      throw new Error(err)
    }
  }

  public createSelfGeneratedAccount() {
    const privateKey = generatePrivateKey()
    const account = privateKeyToAccount(privateKey)

    return { address: account.address, privateKey }
  }

  private async findExceptions(params: FindExceptionsInput): Promise<FindExceptionsResponse> {
    try {
      const exceptions: AirdropList[] = []

      const chunks = Airdropper.getChunks(params.airdropList.length, params.batchSize.toString())
      const fee = await this.getFee(params.airdropList.length)

      for (const [from, to] of chunks) {
        const chunk = params.airdropList.slice(from, to)
        const total = chunk.reduce(Airdropper.getTotalAmount, etherNumbers.BG_ZERO)

        const validateData = await this.validateAsset({
          ...params,
          total,
          airdropList: chunk,
          fee: fee * BigInt(chunk.length),
        })

        if (validateData.isDeflationary && !params.isDeflationary) {
          return this.findExceptions({ ...params, isDeflationary: validateData.isDeflationary })
        }

        exceptions.push(validateData.exceptions)
      }

      return {
        batchSize: params.batchSize,
        exceptions: exceptions.flat(),
        isDeflationary: params.isDeflationary,
      }
    } catch (err) {
      const _batchSize = params.batchSize ? params.batchSize - BigInt(10) : undefined
      if (_batchSize && _batchSize > BigInt(10)) {
        return this.findExceptions({ ...params, batchSize: _batchSize })
      }
      throw err
    }
  }

  private async getAirdropOptions({
    retryAttempt = MAX_ATTEMPT,
    ...params
  }: GetAirdropOptionsInput): Promise<AirdropOptions> {
    try {
      const fee = await this.getFee(10)

      const testList1 = getRandomAddresses(10)
      const testList1Total = testList1.reduce(Airdropper.getTotalAmount, etherNumbers.BG_ZERO)

      const testList2 = testList1.slice(0, -1)
      const testList2Total = testList2.reduce(Airdropper.getTotalAmount, etherNumbers.BG_ZERO)

      const [testData1, testData2] = await Promise.all([
        this.validateAsset({
          ...params,
          fee: fee * BigInt(10),
          token: params.token.address,
          airdropList: testList1,
          total: testList1Total,
        }),
        this.validateAsset({
          ...params,
          total: testList2Total,
          airdropList: testList2,
          fee: fee * BigInt(9),
          token: params.token.address,
        }),
      ])

      const isDeflationary = testData1.isDeflationary || testData2.isDeflationary

      const gasForOne = testData1.gas - testData2.gas
      const batchSize = this.contractConfig.maxGasLimit / gasForOne

      return { batchSize, isDeflationary }
    } catch (err) {
      if (retryAttempt === 1) {
        const _batchSize = defaultCount[this.chain.id as keyof typeof defaultCount]
        return { batchSize: _batchSize, isDeflationary: false }
      }

      return this.getAirdropOptions({ ...params, retryAttempt: retryAttempt - 1 })
    }
  }

  private async validateAsset(params: ValidateMethodInput) {
    if (isEther(params.token)) {
      const data = await this.validateEther(params)
      return data
    }

    if (params.isDeflationary) {
      const data = await this.validateDeflationary(params)
      return data
    }

    const data = await this.validateToken(params)
    return data
  }

  private async validateEther(params: ValidateMethodInput): Promise<ValidateData> {
    try {
      const [gasLeft, exceptions] = await this.readContract<[bigint, AirdropList]>(
        'validateEther',
        [params.airdropList, params.fee],
        params.customer,
        params.fee + params.total,
      )

      const gas = this.contractConfig.maxGasLimit - gasLeft

      return { exceptions, gas }
    } catch (err) {
      throw err
    }
  }

  private async validateToken(params: ValidateMethodInput): Promise<ValidateData> {
    try {
      const [isDeflationary, gasLeft, exceptions] = await this.readContract<[boolean, bigint, AirdropList]>(
        'validateToken',
        [params.token, params.total, params.airdropList],
        params.customer,
      )
      const gas = this.contractConfig.maxGasLimit - gasLeft

      return { exceptions, gas, isDeflationary }
    } catch (err) {
      throw err
    }
  }

  private async validateDeflationary(params: ValidateMethodInput): Promise<ValidateData> {
    try {
      const [gasLeft, exceptions] = await this.readContract<[bigint, AirdropList]>(
        'validateDeflationaryToken',
        [params.token, params.airdropList],
        params.customer,
      )

      const gas = this.contractConfig.maxGasLimit - gasLeft

      return { exceptions, gas }
    } catch (err) {
      throw err
    }
  }

  private async createAirdropTxData(params: CreateAssetCalldataInput) {
    if (isEther(params.token.address)) {
      const txData = await this.createAirdropEtherTxData(params)
      return txData
    }

    if (params.isDeflationary) {
      const txData = await this.createAirdropDeflationaryTxData(params)
      return txData
    }

    const txData = await this.createAirdropTokenTxData(params)
    return txData
  }

  private async createAirdropWithSignatureTxData(params: CreateAssetCalldataInputWithSignature, sender: Hash) {
    if (isEther(params.token.address)) {
      const txData = await this.createAirdropEtherWithSignatureTxData(params, sender)
      return txData
    }

    if (params.isDeflationary) {
      const txData = await this.createAirdropDeflationaryWithSignatureTxData(params, sender)
      return txData
    }

    const txData = await this.createAirdropTokenWithSignatureTxData(params, sender)
    return txData
  }

  private async createAirdropEtherTxData(params: CreateAssetCalldataInput) {
    try {
      const value = params.fee + params.total

      const _params = {
        args: [params.airdropList, params.fee],
        abi: this.contractConfig.abi,
        functionName: 'distributionEther',
      }

      const estimatedGas = await this.provider.estimateContractGas({
        ..._params,
        value,
        account: params.customer,
        address: this.contractConfig.address,
      })

      const calldata = encodeFunctionData(_params)

      return { calldata, estimateGas: Airdropper.bumpGas(estimatedGas), value }
    } catch (err) {
      throw new Error(err)
    }
  }

  private async createAirdropDeflationaryTxData(params: CreateAssetCalldataInput) {
    try {
      const args = [params.token.address, params.airdropList, params.fee]

      const _params = {
        args,
        abi: this.contractConfig.abi,
        functionName: 'distributionDeflationaryToken',
      }
      const estimatedGas = await this.provider.estimateContractGas({
        ..._params,
        value: params.fee,
        account: params.customer,
        address: this.contractConfig.address,
      })

      const calldata = encodeFunctionData(_params)

      return { calldata, estimateGas: Airdropper.bumpGas(estimatedGas, 40), value: params.fee }
    } catch (err) {
      throw new Error(err)
    }
  }

  private async createAirdropTokenTxData(params: CreateAssetCalldataInput) {
    try {
      const args = [params.token.address, params.total, params.airdropList, params.fee]
      const _params = {
        args,
        abi: this.contractConfig.abi,
        functionName: 'distributionToken',
      }
      const estimatedGas = await this.provider.estimateContractGas({
        ..._params,
        value: params.fee,
        account: params.customer,
        address: this.contractConfig.address,
      })

      const calldata = encodeFunctionData(_params)

      return { calldata, estimateGas: Airdropper.bumpGas(estimatedGas), value: params.fee }
    } catch (err) {
      throw new Error(err)
    }
  }

  private async createAirdropEtherWithSignatureTxData(params: CreateAssetCalldataInputWithSignature, sender: Hash) {
    try {
      const value = params.fee + params.total
      const args = [params.airdropList, params.signature, params.expiration, params.fee]

      const _params = {
        args,
        abi: this.contractConfig.abi,
        functionName: 'distributionEtherWithSignature',
      }
      const estimatedGas = await this.provider.estimateContractGas({
        ..._params,
        value,
        account: sender,
        address: this.contractConfig.address,
      })

      const calldata = encodeFunctionData(_params)

      return { calldata, estimateGas: Airdropper.bumpGas(estimatedGas), value }
    } catch (err) {
      throw new Error(err)
    }
  }

  private async createAirdropDeflationaryWithSignatureTxData(
    params: CreateAssetCalldataInputWithSignature,
    sender: Hash,
  ) {
    try {
      const args = [params.token.address, params.airdropList, params.signature, params.expiration, params.fee]

      const _params = {
        args,
        abi: this.contractConfig.abi,
        functionName: 'distributionDeflationaryTokenWithSignature',
      }
      const estimatedGas = await this.provider.estimateContractGas({
        ..._params,
        account: sender,
        value: params.fee,
        address: this.contractConfig.address,
      })

      const calldata = encodeFunctionData(_params)

      return { calldata, estimateGas: Airdropper.bumpGas(estimatedGas, 40), value: params.fee }
    } catch (err) {
      throw new Error(err)
    }
  }

  private async createAirdropTokenWithSignatureTxData(params: CreateAssetCalldataInputWithSignature, sender: Hash) {
    try {
      const args = [
        params.token.address,
        params.total,
        params.airdropList,
        params.signature,
        params.expiration,
        params.fee,
      ]

      const _params = {
        args,
        abi: this.contractConfig.abi,
        functionName: 'distributionTokenWithSignature',
      }
      const estimatedGas = await this.provider.estimateContractGas({
        ..._params,
        account: sender,
        value: params.fee,
        address: this.contractConfig.address,
      })

      const calldata = encodeFunctionData(_params)
      return { calldata, estimateGas: Airdropper.bumpGas(estimatedGas), value: params.fee }
    } catch (err) {
      throw new Error(err)
    }
  }

  private async readContract<T>(functionName: string, args: any[], account?: Hash, value?: bigint): Promise<T> {
    return (
      await this.provider.simulateContract({
        args,
        value,
        account,
        functionName,
        chain: this.chain,
        abi: this.contractConfig.abi,
        address: this.contractConfig.address,
        gas: this.contractConfig.maxGasLimit,
      })
    ).result as T
  }

  private getPriceCoefficient(length: number) {
    if (length < 200) {
      return 75
    } else if (length >= 200 && length < 1200) {
      return 50
    }
    return 25
  }
}

export const airdropper = new Airdropper()
